"use client";
import { useState, useEffect, useRef, useCallback } from "react";
import { useDispatch, useSelector } from "react-redux";
import { fetchSales, fetchSaleDetails, processReturn, addPayment, reset, clearCurrentSale } from "@/state/sales/sale.slice";
import { fetchUsers } from "@/state/users/users.slice";
import { fetchBusiness } from "@/state/business/business.slice";
import { getShops } from "@/state/shops/shop.slice";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { Badge } from "@/components/ui/badge";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  Sheet,
  SheetContent,
  SheetDescription,
  SheetHeader,
  SheetTitle,
} from "@/components/ui/sheet";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  DotsThreeVertical,
  MagnifyingGlass,
  Plus,
  Printer,
  Eye,
  ArrowClockwise,
  CreditCard,
  Receipt as ReceiptIcon,
} from "@phosphor-icons/react";
import { format } from "date-fns";
import { Link } from "react-router-dom";
import PermissionGuard from "@/components/custom/common/PermissionGuard";
import { User, Store, Calendar, RotateCcw, Filter, X, Loader2, RefreshCcw, Undo2, DollarSign, FileText, Pencil, Trash2, Share2 } from "lucide-react";
import Receipt from "@/components/custom/sales/Receipt";
import { useReactToPrint } from "react-to-print";
import { useToast } from "@/components/ui/use-toast";
import { usePermission } from "@/hooks/usePermission";
import { useDebounce } from "@/hooks/useDebounce";
import { numberWithCommas } from "@/lib/formatCommas";
import axios from "axios";

export default function SalesListPage() {
  const dispatch = useDispatch();
  const { toast } = useToast();
  const { sales, loading, pagination, summary, currentSale, success, error, message } = useSelector((state) => state.sales);
  const { business } = useSelector((state) => state.business || {});
  const { shops } = useSelector((state) => state.shop || { shops: [] });
  const { users } = useSelector((state) => state.users || { users: [] });
  const currentUser = useSelector((state) => state.auth.user);

  const [searchTerm, setSearchTerm] = useState("");
  const debouncedSearch = useDebounce(searchTerm, 400);
  const [productSearch, setProductSearch] = useState("");
  const debouncedProductSearch = useDebounce(productSearch, 400);
  const [shopFilter, setShopFilter] = useState("");
  const [userFilter, setUserFilter] = useState("");
  const [page, setPage] = useState(1);
  const [limit] = useState(20);
  // Default to today's date - user can expand with filter
  const [startDate, setStartDate] = useState(format(new Date(), "yyyy-MM-dd"));
  const [endDate, setEndDate] = useState(format(new Date(), "yyyy-MM-dd"));

  // Dialog/Sheet states
  const [detailsSheetOpen, setDetailsSheetOpen] = useState(false);
  const [returnDialogOpen, setReturnDialogOpen] = useState(false);
  const [paymentDialogOpen, setPaymentDialogOpen] = useState(false);
  const [paymentHistorySheetOpen, setPaymentHistorySheetOpen] = useState(false);
  const [selectedSale, setSelectedSale] = useState(null);

  // Permissions
  const canAcceptPayments = usePermission('AcceptPayments');
  const canManagePayments = usePermission('ManagePayments');
  const canViewAllSales = usePermission('ViewAllSales');
  const canAcceptCrossShopPayments = usePermission('AcceptCrossShopPayments');
  const canProcessReturns = usePermission('Returns');
  const canProcessCrossShopReturns = usePermission('ProcessCrossShopReturns');

  // Filter shops based on user permissions
  const viewableShops = canViewAllSales
    ? shops
    : shops?.filter(shop => {
        const userShopId = currentUser?.shop_id;
        const allowedShops = currentUser?.allowed_shops || [];
        return shop.id === userShopId || allowedShops.includes(shop.id);
      }) || [];

  // Form states
  const [returnReason, setReturnReason] = useState("");
  const [returnItems, setReturnItems] = useState([]);
  const [returnType, setReturnType] = useState("full"); // 'full' or 'partial'
  const [paymentAmount, setPaymentAmount] = useState("");
  const [paymentMethod, setPaymentMethod] = useState("Cash");
  const [paymentNotes, setPaymentNotes] = useState("");
  const [editingPayment, setEditingPayment] = useState(null);
  const [deletePaymentId, setDeletePaymentId] = useState(null);
  const [isPaymentSubmitting, setIsPaymentSubmitting] = useState(false);

  // Print Logic
  const [selectedSaleForPrint, setSelectedSaleForPrint] = useState(null);
  const receiptRef = useRef();

  const handlePrint = useReactToPrint({
    contentRef: receiptRef,
    onAfterPrint: () => setSelectedSaleForPrint(null),
    pageStyle: `
      @page { size: auto; margin: 10mm; }
      @media print {
        body { -webkit-print-color-adjust: exact; print-color-adjust: exact; }
      }
    `
  });

  const onPrintReceipt = async (sale) => {
    // Fetch full sale details (with items) before printing
    try {
      await dispatch(fetchSaleDetails(sale.id)).unwrap();
    } catch (err) {
      toast({ variant: "destructive", title: "Error", description: "Failed to load sale details" });
      return;
    }
  };

  // Effect to trigger print when currentSale is loaded for printing
  useEffect(() => {
    if (currentSale && selectedSaleForPrint?.id === currentSale.id) {
      setTimeout(() => {
        handlePrint();
      }, 200);
    }
  }, [currentSale, selectedSaleForPrint]);

  // Handle print after fetching details
  useEffect(() => {
    if (currentSale && !detailsSheetOpen && !selectedSaleForPrint) {
      // This is for the print flow - set the sale for print
    }
  }, [currentSale]);

  const buildSalesParams = useCallback(() => ({
    page, limit, search: debouncedSearch, startDate, endDate,
    shopId: shopFilter || undefined, userId: userFilter || undefined,
    productSearch: debouncedProductSearch || undefined,
  }), [page, limit, debouncedSearch, startDate, endDate, shopFilter, userFilter, debouncedProductSearch]);

  useEffect(() => {
    dispatch(fetchBusiness());
    dispatch(getShops());
    dispatch(fetchUsers());
  }, [dispatch]);

  useEffect(() => {
    dispatch(fetchSales(buildSalesParams()));
  }, [dispatch, buildSalesParams]);

  useEffect(() => {
    if (error) {
      toast({ variant: "destructive", title: "Error", description: error });
      dispatch(reset());
    }
    if (success && message) {
      toast({ title: "Success", description: message });
      dispatch(reset());
      dispatch(fetchSales(buildSalesParams()));
    }
  }, [error, success, message, toast, dispatch, buildSalesParams]);

  const handleRefresh = () => {
    dispatch(fetchSales(buildSalesParams()));
  };

  const handleSearch = (e) => {
    setSearchTerm(e.target.value);
    setPage(1);
  };

  const statusColor = (status) => {
    switch (status) {
      case "paid": return "bg-green-100 text-green-800";
      case "partial": return "bg-yellow-100 text-yellow-800";
      case "unpaid": return "bg-red-100 text-red-800";
      default: return "bg-gray-100 dark:bg-slate-800 text-gray-800";
    }
  };

  const saleStatusColor = (status) => {
    switch (status) {
      case "completed": return "bg-blue-100 text-blue-800";
      case "partial_return": return "bg-orange-100 text-orange-800";
      case "returned": return "bg-red-100 text-red-800";
      case "pending": return "bg-yellow-100 text-yellow-800";
      default: return "bg-gray-100 dark:bg-slate-800 text-gray-800";
    }
  };

  // Action handlers
  const handleViewDetails = async (sale) => {
    setSelectedSale(sale);
    try {
      await dispatch(fetchSaleDetails(sale.id)).unwrap();
      setDetailsSheetOpen(true);
    } catch (err) {
      toast({ variant: "destructive", title: "Error", description: "Failed to load sale details" });
    }
  };

  const handlePrintReceipt = async (sale) => {
    setSelectedSaleForPrint(sale);
    try {
      await dispatch(fetchSaleDetails(sale.id)).unwrap();
    } catch (err) {
      toast({ variant: "destructive", title: "Error", description: "Failed to load sale details" });
      setSelectedSaleForPrint(null);
    }
  };

  // Generate receipt text for sharing
  const generateReceiptText = (sale) => {
    const items = sale.SaleItems || [];
    const itemsText = items.map(item =>
      `• ${item.title || item.name || 'Item'} x${item.qty} - ${currency} ${parseFloat(item.total).toFixed(2)}`
    ).join('\n');

    const receiptText = `
🧾 *${businessDetails?.name || 'Receipt'}*
━━━━━━━━━━━━━━━━━━
📋 Invoice: ${sale.order_number}
📅 Date: ${format(new Date(sale.createdAt), 'dd MMM yyyy HH:mm')}
👤 Customer: ${sale.Customer?.name || sale.billing_fname || 'Walk-in'}

*Items:*
${itemsText}

━━━━━━━━━━━━━━━━━━
💰 *Total: ${currency} ${parseFloat(sale.total).toFixed(2)}*
📊 Status: ${sale.payment_status?.toUpperCase()}

Thank you for your business! 🙏
`.trim();

    return receiptText;
  };

  const handleShareWhatsApp = async (sale) => {
    try {
      // Fetch full sale details if not already loaded
      let saleWithItems = sale;
      if (!sale.SaleItems || sale.SaleItems.length === 0) {
        const result = await dispatch(fetchSaleDetails(sale.id)).unwrap();
        saleWithItems = result;
      }

      const receiptText = generateReceiptText(saleWithItems);
      const encodedText = encodeURIComponent(receiptText);

      // Check if customer has a phone number for direct messaging
      const customerPhone = saleWithItems.Customer?.phone?.replace(/[^0-9]/g, '');

      if (customerPhone) {
        window.open(`https://wa.me/${customerPhone}?text=${encodedText}`, '_blank');
      } else {
        window.open(`https://wa.me/?text=${encodedText}`, '_blank');
      }
    } catch (err) {
      toast({ variant: "destructive", title: "Error", description: "Failed to share receipt" });
    }
  };

  const handleShareTelegram = async (sale) => {
    try {
      // Fetch full sale details if not already loaded
      let saleWithItems = sale;
      if (!sale.SaleItems || sale.SaleItems.length === 0) {
        const result = await dispatch(fetchSaleDetails(sale.id)).unwrap();
        saleWithItems = result;
      }

      const receiptText = generateReceiptText(saleWithItems);
      const encodedText = encodeURIComponent(receiptText);

      window.open(`https://t.me/share/url?url=&text=${encodedText}`, '_blank');
    } catch (err) {
      toast({ variant: "destructive", title: "Error", description: "Failed to share receipt" });
    }
  };

  const handleOpenReturn = async (sale) => {
    setSelectedSale(sale);
    setReturnReason("");
    setReturnType("full");
    try {
      const result = await dispatch(fetchSaleDetails(sale.id)).unwrap();
      // Initialize return items from sale items
      const items = (result.SaleItems || []).map(item => ({
        product_id: item.product_id,
        title: item.title,
        qty: item.qty,
        returned_qty: item.returned_qty || 0,
        available_to_return: item.qty - (item.returned_qty || 0),
        qty_to_return: item.qty - (item.returned_qty || 0), // Default to full return
        price: item.product_price
      }));
      setReturnItems(items);
      setReturnDialogOpen(true);
    } catch (err) {
      toast({ variant: "destructive", title: "Error", description: "Failed to load sale items" });
    }
  };

  const handleOpenPayment = (sale) => {
    setSelectedSale(sale);
    setPaymentAmount("");
    setPaymentMethod("Cash");
    setPaymentNotes("");
    setPaymentDialogOpen(true);
  };

  const handleViewPaymentHistory = async (sale) => {
    setSelectedSale(sale);
    try {
      await dispatch(fetchSaleDetails(sale.id)).unwrap();
      setPaymentHistorySheetOpen(true);
    } catch (err) {
      toast({ variant: "destructive", title: "Error", description: "Failed to load payment history" });
    }
  };

  const handleSubmitReturn = async () => {
    if (!selectedSale) return;
    try {
      const itemsToReturn = returnType === 'full'
        ? [] // Empty means full return
        : returnItems
          .filter(item => item.qty_to_return > 0)
          .map(item => ({
            product_id: item.product_id,
            qty_to_return: item.qty_to_return
          }));

      if (returnType === 'partial' && itemsToReturn.length === 0) {
        toast({ variant: "destructive", title: "Error", description: "Select at least one item to return" });
        return;
      }

      await dispatch(processReturn({
        id: selectedSale.id,
        data: {
          reason: returnReason,
          items: itemsToReturn
        }
      })).unwrap();
      setReturnDialogOpen(false);
    } catch (err) {
      // Error handled by useEffect
    }
  };

  const handleSubmitPayment = async () => {
    if (!selectedSale || !paymentAmount) return;
    try {
      await dispatch(addPayment({
        sale_id: selectedSale.id,
        amount: parseFloat(paymentAmount),
        payment_method: paymentMethod,
        notes: paymentNotes,
      })).unwrap();
      setPaymentDialogOpen(false);
    } catch (err) {
      // Error handled by useEffect
    }
  };

  const handleEditPayment = (payment) => {
    setEditingPayment(payment);
    setPaymentAmount(payment.amount.toString());
    setPaymentMethod(payment.payment_method);
    setPaymentNotes(payment.notes || "");
    setPaymentDialogOpen(true);
  };

  const handleUpdatePayment = async () => {
    if (!editingPayment || !paymentAmount) return;
    setIsPaymentSubmitting(true);
    try {
      await axios.put(`${import.meta.env.VITE_API_URL}payments/${editingPayment.id}`, {
        amount: parseFloat(paymentAmount),
        payment_method: paymentMethod,
        notes: paymentNotes,
      }, { withCredentials: true });

      toast({ title: "Success", description: "Payment updated successfully" });
      setPaymentDialogOpen(false);
      setEditingPayment(null);
      setPaymentAmount("");
      // Refresh sale details - use currentSale.id since we're in the payment history sheet
      if (currentSale) {
        dispatch(fetchSaleDetails(currentSale.id));
      } else if (selectedSale) {
        dispatch(fetchSaleDetails(selectedSale.id));
      }
      dispatch(fetchSales(buildSalesParams()));
    } catch (err) {
      toast({ variant: "destructive", title: "Error", description: err.response?.data?.message || "Failed to update payment" });
    } finally {
      setIsPaymentSubmitting(false);
    }
  };

  const handleDeletePayment = async () => {
    if (!deletePaymentId) return;
    setIsPaymentSubmitting(true);
    try {
      await axios.delete(`${import.meta.env.VITE_API_URL}payments/${deletePaymentId}`, { withCredentials: true });
      toast({ title: "Success", description: "Payment deleted successfully" });
      setDeletePaymentId(null);
      // Refresh sale details - use currentSale.id since we're in the payment history sheet
      if (currentSale) {
        dispatch(fetchSaleDetails(currentSale.id));
      } else if (selectedSale) {
        dispatch(fetchSaleDetails(selectedSale.id));
      }
      dispatch(fetchSales(buildSalesParams()));
    } catch (err) {
      toast({ variant: "destructive", title: "Error", description: err.response?.data?.message || "Failed to delete payment" });
    } finally {
      setIsPaymentSubmitting(false);
    }
  };

  const businessDetails = business || null;
  const currency = businessDetails?.currency || "GHS";

  return (
    <div className="mx-auto p-4 space-y-4">
      {/* Header */}
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-3">
        <div>
          <h1 className="text-2xl font-bold">Sales</h1>
          <p className="text-sm text-muted-foreground">Manage and track all sales transactions</p>
        </div>
        <div className="flex gap-2">
          <Button variant="outline" size="sm" onClick={handleRefresh} disabled={loading}>
            <RefreshCcw className={`h-4 w-4 ${loading ? 'animate-spin' : ''}`} />
          </Button>
          <Link to="/dashboard/sales/create">
            <Button size="sm">
              <Plus className="mr-2 h-4 w-4" /> New Sale
            </Button>
          </Link>
        </div>
      </div>

      {/* Compact Filters Bar */}
      <Card className="p-3">
        <div className="flex flex-wrap gap-2 items-center">
          {/* Search */}
          <div className="flex items-center space-x-2 bg-muted/30 px-2 rounded-md border flex-1 min-w-[200px] max-w-[300px]">
            <MagnifyingGlass className="w-4 h-4 text-muted-foreground" />
            <Input
              placeholder="Search sales..."
              value={searchTerm}
              onChange={handleSearch}
              className="border-none shadow-none focus-visible:ring-0 h-8 bg-transparent"
            />
          </div>

          {/* Product Search */}
          <div className="flex items-center space-x-2 bg-muted/30 px-2 rounded-md border flex-1 min-w-[200px] max-w-[300px]">
            <MagnifyingGlass className="w-4 h-4 text-muted-foreground" />
            <Input
              placeholder="Search by product..."
              value={productSearch}
              onChange={(e) => { setProductSearch(e.target.value); setPage(1); }}
              className="border-none shadow-none focus-visible:ring-0 h-8 bg-transparent"
            />
            {productSearch && (
              <button onClick={() => { setProductSearch(""); setPage(1); }} className="text-muted-foreground hover:text-foreground">
                <X className="w-3 h-3" />
              </button>
            )}
          </div>

          {/* Shop Filter */}
          {viewableShops?.length > 0 && (
            <Select value={shopFilter} onValueChange={(val) => { setShopFilter(val === 'all' ? '' : val); setPage(1); }}>
              <SelectTrigger className="w-[120px] h-8 text-xs">
                <SelectValue placeholder="All Shops" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Shops</SelectItem>
                {viewableShops.map((shop) => (
                  <SelectItem key={shop.id} value={shop.id.toString()}>
                    {shop.name}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          )}

          {/* User Filter */}
          <PermissionGuard required="ViewAllSales">
            <Select value={userFilter} onValueChange={(val) => { setUserFilter(val === 'all' ? '' : val); setPage(1); }}>
              <SelectTrigger className="w-[120px] h-8 text-xs">
                <SelectValue placeholder="All Users" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Users</SelectItem>
                {users?.map((user) => (
                  <SelectItem key={user.id} value={user.id.toString()}>
                    {user.username || `${user.first_name} ${user.last_name}`}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </PermissionGuard>

          {/* Date Range */}
          <div className="flex items-center gap-1 bg-muted/30 px-2 rounded-md border">
            <Input
              type="date"
              value={startDate}
              onChange={(e) => { setStartDate(e.target.value); setPage(1); }}
              className="w-[110px] h-7 bg-transparent border-none shadow-none focus-visible:ring-0 p-0 text-xs"
            />
            <span className="text-muted-foreground text-xs">to</span>
            <Input
              type="date"
              value={endDate}
              onChange={(e) => { setEndDate(e.target.value); setPage(1); }}
              className="w-[110px] h-7 bg-transparent border-none shadow-none focus-visible:ring-0 p-0 text-xs"
            />
          </div>

          {/* Quick Date Filters */}
          <div className="flex gap-1">
            <Button variant="ghost" size="sm" className="h-7 px-2 text-xs" onClick={() => {
              const today = format(new Date(), "yyyy-MM-dd");
              setStartDate(today); setEndDate(today); setPage(1);
            }}>Today</Button>
            <Button variant="ghost" size="sm" className="h-7 px-2 text-xs" onClick={() => {
              const today = new Date();
              const weekAgo = new Date(today); weekAgo.setDate(today.getDate() - 7);
              setStartDate(format(weekAgo, "yyyy-MM-dd")); setEndDate(format(today, "yyyy-MM-dd")); setPage(1);
            }}>Week</Button>
            <Button variant="ghost" size="sm" className="h-7 px-2 text-xs" onClick={() => {
              const today = new Date();
              const monthAgo = new Date(today); monthAgo.setMonth(today.getMonth() - 1);
              setStartDate(format(monthAgo, "yyyy-MM-dd")); setEndDate(format(today, "yyyy-MM-dd")); setPage(1);
            }}>Month</Button>
          </div>
        </div>
      </Card>

      {/* Compact Summary Cards */}
      {summary && (
        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-2">
          <Card className="p-2 bg-blue-50 dark:bg-blue-900/20 border-blue-200 dark:border-blue-800">
            <p className="text-[10px] text-blue-600 dark:text-blue-400 font-medium">Total Sales</p>
            <p className="text-lg font-bold text-blue-900 dark:text-blue-100">{numberWithCommas(summary.totalSales)}</p>
          </Card>
          <Card className="p-2 bg-slate-50 dark:bg-slate-900/20 border-slate-200 dark:border-slate-800">
            <p className="text-[10px] text-slate-600 dark:text-slate-400 font-medium">Gross Revenue</p>
            <p className="text-lg font-bold text-slate-900 dark:text-slate-100">{currency} {numberWithCommas(parseFloat(summary.grossRevenue || 0).toFixed(2))}</p>
          </Card>
          <Card className="p-2 bg-green-50 dark:bg-green-900/20 border-green-200 dark:border-green-800">
            <p className="text-[10px] text-green-600 dark:text-green-400 font-medium">Net Revenue</p>
            <p className="text-lg font-bold text-green-900 dark:text-green-100">{currency} {numberWithCommas(parseFloat(summary.netRevenue || 0).toFixed(2))}</p>
          </Card>
          <PermissionGuard required="ViewCostPrice">
            {summary.totalProfit !== null && (
              <Card className={`p-2 border ${summary.totalProfit >= 0 ? 'bg-emerald-50 dark:bg-emerald-900/20 border-emerald-200 dark:border-emerald-800' : 'bg-red-50 dark:bg-red-900/20 border-red-200 dark:border-red-800'}`}>
                <p className={`text-[10px] font-medium ${summary.totalProfit >= 0 ? 'text-emerald-600 dark:text-emerald-400' : 'text-red-600 dark:text-red-400'}`}>Profit</p>
                <p className={`text-lg font-bold ${summary.totalProfit >= 0 ? 'text-emerald-900 dark:text-emerald-100' : 'text-red-900 dark:text-red-100'}`}>{currency} {numberWithCommas(parseFloat(summary.totalProfit || 0).toFixed(2))}</p>
              </Card>
            )}
          </PermissionGuard>
          <Card className="p-2 bg-orange-50 dark:bg-orange-900/20 border-orange-200 dark:border-orange-800">
            <p className="text-[10px] text-orange-600 dark:text-orange-400 font-medium">Returns</p>
            <p className="text-lg font-bold text-orange-900 dark:text-orange-100">{numberWithCommas(summary.totalReturns)}</p>
            <p className="text-[9px] text-orange-500">{currency} {numberWithCommas(parseFloat(summary.returnsValue || 0).toFixed(2))}</p>
          </Card>
          <Card className="p-2 bg-red-50 dark:bg-red-900/20 border-red-200 dark:border-red-800">
            <p className="text-[10px] text-red-600 dark:text-red-400 font-medium">Outstanding</p>
            <p className="text-lg font-bold text-red-900 dark:text-red-100">{currency} {numberWithCommas(parseFloat(summary.totalOutstanding || 0).toFixed(2))}</p>
            <p className="text-[9px] text-red-500">{numberWithCommas(summary.creditSalesCount)} credit sales</p>
          </Card>
        </div>
      )}

      <Card className="enterprise-table-container p-0">
        <div>
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Date</TableHead>
                <TableHead>Invoice #</TableHead>
                <TableHead>Customer</TableHead>
                <TableHead>Shop</TableHead>
                <TableHead>Cashier</TableHead>
                <TableHead>Total</TableHead>
                <TableHead>Payment</TableHead>
                <TableHead>Status</TableHead>
                <TableHead className="text-right">Actions</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {loading && sales?.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={9} className="h-24 text-center">
                    <span className="flex items-center justify-center gap-2"><Loader2 className="animate-spin" /> Loading sales...</span>
                  </TableCell>
                </TableRow>
              ) : sales?.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={9} className="h-24 text-center text-muted-foreground">
                    No sales found.
                  </TableCell>
                </TableRow>
              ) : (
                sales?.map((sale) => (
                  <TableRow key={sale.id} className={sale.status === 'returned' ? 'opacity-60' : ''}>
                    <TableCell>{format(new Date(sale.createdAt), "dd MMM yyyy HH:mm")}</TableCell>
                    <TableCell className="font-mono">{sale.order_number}</TableCell>
                    <TableCell>{sale.Customer?.name || sale.billing_fname || "Walk-in"}</TableCell>
                    <TableCell className="text-sm text-muted-foreground">{sale.shop?.name || "-"}</TableCell>
                    <TableCell>{sale.User?.username || "N/A"}</TableCell>
                    <TableCell className="font-bold">{currency} {parseFloat(sale.total).toFixed(2)}</TableCell>
                    <TableCell>
                      <Badge className={statusColor(sale.payment_status)} variant="outline">
                        {sale.payment_status?.toUpperCase()}
                      </Badge>
                    </TableCell>
                    <TableCell>
                      <Badge className={saleStatusColor(sale.status)} variant="outline">
                        {sale.status?.toUpperCase() || 'COMPLETED'}
                      </Badge>
                    </TableCell>
                    <TableCell className="text-right relative">
                      <DropdownMenu>
                        <DropdownMenuTrigger asChild>
                          <Button variant="ghost" className="h-8 w-8 p-0">
                            <DotsThreeVertical className="h-4 w-4" />
                          </Button>
                        </DropdownMenuTrigger>
                        <DropdownMenuContent align="end">
                          <DropdownMenuLabel>Actions</DropdownMenuLabel>
                          <DropdownMenuItem onClick={() => handleViewDetails(sale)}>
                            <Eye className="mr-2 h-4 w-4" /> View Details
                          </DropdownMenuItem>
                          <DropdownMenuItem onClick={() => handleViewPaymentHistory(sale)}>
                            <FileText className="mr-2 h-4 w-4" /> Payment History
                          </DropdownMenuItem>
                          <DropdownMenuItem onClick={() => handlePrintReceipt(sale)}>
                            <Printer className="mr-2 h-4 w-4" /> Print Receipt
                          </DropdownMenuItem>
                          <DropdownMenuItem onClick={() => handleShareWhatsApp(sale)}>
                            <Share2 className="mr-2 h-4 w-4 text-green-600" /> Share via WhatsApp
                          </DropdownMenuItem>
                          <DropdownMenuItem onClick={() => handleShareTelegram(sale)}>
                            <Share2 className="mr-2 h-4 w-4 text-blue-500" /> Share via Telegram
                          </DropdownMenuItem>
                          <DropdownMenuSeparator />
                          {canAcceptPayments && (sale.payment_status === 'partial' || sale.payment_status === 'unpaid') && sale.status !== 'returned' && (canAcceptCrossShopPayments || !sale.shop_id || sale.shop_id === currentUser?.shop_id) && (
                            <DropdownMenuItem onClick={() => handleOpenPayment(sale)}>
                              <DollarSign className="mr-2 h-4 w-4" /> Add Payment
                            </DropdownMenuItem>
                          )}
                          {canProcessReturns && sale.status !== 'returned' && (canProcessCrossShopReturns || !sale.shop_id || sale.shop_id === currentUser?.shop_id) && (
                            <DropdownMenuItem onClick={() => handleOpenReturn(sale)} className="text-red-600">
                              <Undo2 className="mr-2 h-4 w-4" /> Process Return
                            </DropdownMenuItem>
                          )}
                        </DropdownMenuContent>
                      </DropdownMenu>
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </div>

        {/* Pagination Controls */}
        <div className="flex items-center justify-end space-x-2 py-4 px-4">
          <Button
            variant="outline"
            size="sm"
            onClick={() => setPage(p => Math.max(1, p - 1))}
            disabled={page === 1 || loading}
          >
            Previous
          </Button>
          <div className="text-sm text-center min-w-[100px]">
            Page {page} of {pagination?.totalPages || 1}
          </div>
          <Button
            variant="outline"
            size="sm"
            onClick={() => setPage(p => p + 1)}
            disabled={page >= (pagination?.totalPages || 1) || loading}
          >
            Next
          </Button>
        </div>
      </Card>

      {/* Sale Details Sheet */}
      <Sheet open={detailsSheetOpen} onOpenChange={setDetailsSheetOpen}>
        <SheetContent className="sm:max-w-lg overflow-y-auto">
          <SheetHeader>
            <SheetTitle>Sale Details</SheetTitle>
            <SheetDescription>
              {currentSale?.order_number} - {currentSale?.createdAt ? format(new Date(currentSale.createdAt), "dd MMM yyyy HH:mm") : ''}
            </SheetDescription>
          </SheetHeader>
          {currentSale && (
            <div className="space-y-6 mt-6">
              {/* Customer Info */}
              <div className="space-y-2">
                <h4 className="font-semibold text-sm text-muted-foreground">Customer</h4>
                <p className="font-medium">{currentSale.Customer?.name || currentSale.billing_fname || "Walk-in"}</p>
                {currentSale.Customer?.phone && <p className="text-sm text-muted-foreground">{currentSale.Customer.phone}</p>}
              </div>

              {/* Items */}
              <div className="space-y-2">
                <h4 className="font-semibold text-sm text-muted-foreground">Items</h4>
                <div className="border">
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead>Item</TableHead>
                        <TableHead>IMEI/Serial</TableHead>
                        <TableHead className="text-right">Qty</TableHead>
                        <TableHead className="text-right">Price</TableHead>
                        <TableHead className="text-right">Total</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {currentSale.SaleItems?.map((item, idx) => (
                        <TableRow key={idx}>
                          <TableCell>{item.title}</TableCell>
                          <TableCell className="text-xs text-muted-foreground font-mono">{item.imei || '-'}</TableCell>
                          <TableCell className="text-right">{item.qty}</TableCell>
                          <TableCell className="text-right">{currency} {parseFloat(item.product_price).toFixed(2)}</TableCell>
                          <TableCell className="text-right font-medium">{currency} {parseFloat(item.total).toFixed(2)}</TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </div>
              </div>

              {/* Summary */}
              <div className="space-y-2 border-t pt-4">
                <div className="flex justify-between">
                  <span>Subtotal</span>
                  <span>{currency} {(parseFloat(currentSale.total) - parseFloat(currentSale.tax || 0) + parseFloat(currentSale.discount || 0)).toFixed(2)}</span>
                </div>
                {parseFloat(currentSale.tax || 0) > 0 && (
                  <div className="flex justify-between">
                    <span>Tax</span>
                    <span>{currency} {parseFloat(currentSale.tax).toFixed(2)}</span>
                  </div>
                )}
                {parseFloat(currentSale.discount || 0) > 0 && (
                  <div className="flex justify-between text-red-600">
                    <span>Discount</span>
                    <span>-{currency} {parseFloat(currentSale.discount).toFixed(2)}</span>
                  </div>
                )}
                <div className="flex justify-between font-bold text-lg border-t pt-2">
                  <span>Total</span>
                  <span>{currency} {parseFloat(currentSale.total).toFixed(2)}</span>
                </div>
              </div>

              {/* Payment Info */}
              <div className="space-y-2 border-t pt-4">
                <div className="flex justify-between">
                  <span>Payment Method</span>
                  <span>{currentSale.method}</span>
                </div>
                <div className="flex justify-between">
                  <span>Payment Status</span>
                  <Badge className={statusColor(currentSale.payment_status)}>{currentSale.payment_status?.toUpperCase()}</Badge>
                </div>
                <div className="flex justify-between">
                  <span>Sale Status</span>
                  <Badge className={saleStatusColor(currentSale.status)}>{currentSale.status?.toUpperCase() || 'COMPLETED'}</Badge>
                </div>
                {currentSale.due_date && (
                  <div className="flex justify-between">
                    <span>Due Date</span>
                    <span className={new Date(currentSale.due_date) < new Date() ? 'text-red-600 font-semibold' : ''}>
                      {format(new Date(currentSale.due_date), "dd MMM yyyy")}
                      {new Date(currentSale.due_date) < new Date() && ' (OVERDUE)'}
                    </span>
                  </div>
                )}
                {(currentSale.payment_status === 'partial' || currentSale.payment_status === 'unpaid') && (
                  <div className="flex justify-between text-orange-600 font-semibold">
                    <span>Outstanding</span>
                    <span>{currency} {Math.max(0, parseFloat(currentSale.total) - parseFloat(currentSale.amount_paid || 0)).toFixed(2)}</span>
                  </div>
                )}
              </div>

              {/* Payment History */}
              {currentSale.payments && currentSale.payments.length > 0 && (
                <div className="space-y-2 border-t pt-4">
                  <h4 className="font-semibold text-sm text-muted-foreground">Payment History</h4>
                  <div className="border rounded">
                    <Table>
                      <TableHeader>
                        <TableRow>
                          <TableHead>Date</TableHead>
                          <TableHead>Method</TableHead>
                          <TableHead className="text-right">Amount</TableHead>
                        </TableRow>
                      </TableHeader>
                      <TableBody>
                        {currentSale.payments.map((payment, idx) => (
                          <TableRow key={idx}>
                            <TableCell className="text-sm">{format(new Date(payment.payment_date), "dd MMM yyyy")}</TableCell>
                            <TableCell className="text-sm">{payment.payment_method}</TableCell>
                            <TableCell className="text-right font-medium text-green-600">{currency} {parseFloat(payment.amount).toFixed(2)}</TableCell>
                          </TableRow>
                        ))}
                        <TableRow className="bg-muted/50">
                          <TableCell colSpan={2} className="font-semibold">Total Paid</TableCell>
                          <TableCell className="text-right font-bold text-green-700">
                            {currency} {currentSale.payments.reduce((sum, p) => sum + parseFloat(p.amount), 0).toFixed(2)}
                          </TableCell>
                        </TableRow>
                      </TableBody>
                    </Table>
                  </div>
                </div>
              )}

              {/* Actions */}
              <div className="flex flex-col gap-2 pt-4">
                <div className="flex gap-2">
                  <Button variant="outline" className="flex-1" onClick={() => handlePrintReceipt(currentSale)}>
                    <Printer className="mr-2 h-4 w-4" /> Print Receipt
                  </Button>
                  {canAcceptPayments && (currentSale.payment_status === 'partial' || currentSale.payment_status === 'unpaid') && currentSale.status !== 'returned' && (canAcceptCrossShopPayments || !currentSale.shop_id || currentSale.shop_id === currentUser?.shop_id) && (
                    <Button className="flex-1" onClick={() => { setDetailsSheetOpen(false); handleOpenPayment(currentSale); }}>
                      <DollarSign className="mr-2 h-4 w-4" /> Add Payment
                    </Button>
                  )}
                </div>
                <div className="flex gap-2">
                  <Button variant="outline" size="sm" className="flex-1 text-green-600 border-green-200 hover:bg-green-50" onClick={() => handleShareWhatsApp(currentSale)}>
                    <Share2 className="mr-2 h-4 w-4" /> WhatsApp
                  </Button>
                  <Button variant="outline" size="sm" className="flex-1 text-blue-500 border-blue-200 hover:bg-blue-50" onClick={() => handleShareTelegram(currentSale)}>
                    <Share2 className="mr-2 h-4 w-4" /> Telegram
                  </Button>
                </div>
              </div>
            </div>
          )}
        </SheetContent>
      </Sheet>

      {/* Return Dialog */}
      <Dialog open={returnDialogOpen} onOpenChange={setReturnDialogOpen}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle>Process Return</DialogTitle>
            <DialogDescription>
              Return items from sale {selectedSale?.order_number}
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4">
            {/* Return Type Selection */}
            <div className="space-y-2">
              <Label>Return Type</Label>
              <div className="flex gap-4">
                <Button
                  variant={returnType === 'full' ? 'default' : 'outline'}
                  size="sm"
                  onClick={() => setReturnType('full')}
                >
                  Full Return
                </Button>
                <Button
                  variant={returnType === 'partial' ? 'default' : 'outline'}
                  size="sm"
                  onClick={() => setReturnType('partial')}
                >
                  Partial Return
                </Button>
              </div>
            </div>

            {/* Items Selection for Partial Return */}
            {returnType === 'partial' && (
              <div className="space-y-2">
                <Label>Select Items to Return</Label>
                <div className="border rounded max-h-[200px] overflow-y-auto">
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead>Item</TableHead>
                        <TableHead className="w-20 text-center">Sold</TableHead>
                        <TableHead className="w-24 text-center">Available</TableHead>
                        <TableHead className="w-28">Return Qty</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {returnItems.map((item, idx) => (
                        <TableRow key={idx}>
                          <TableCell className="font-medium">{item.title}</TableCell>
                          <TableCell className="text-center">{item.qty}</TableCell>
                          <TableCell className="text-center">
                            {item.available_to_return}
                            {item.returned_qty > 0 && (
                              <span className="text-xs text-muted-foreground ml-1">({item.returned_qty} returned)</span>
                            )}
                          </TableCell>
                          <TableCell>
                            <Input
                              type="number"
                              min="0"
                              max={item.available_to_return}
                              value={item.qty_to_return}
                              onChange={(e) => {
                                const val = Math.min(parseInt(e.target.value) || 0, item.available_to_return);
                                setReturnItems(items => items.map((i, index) =>
                                  index === idx ? { ...i, qty_to_return: val } : i
                                ));
                              }}
                              className="w-20 h-8"
                              disabled={item.available_to_return === 0}
                            />
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </div>
                <p className="text-sm text-muted-foreground">
                  Refund Value: {currency} {returnItems.reduce((sum, i) => sum + (i.qty_to_return * parseFloat(i.price)), 0).toFixed(2)}
                </p>
              </div>
            )}

            {returnType === 'full' && (
              <div className="p-3 bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-800 rounded">
                <p className="text-sm text-amber-800 dark:text-amber-200">
                  This will return ALL items from this sale and restore stock.
                </p>
              </div>
            )}

            <div className="space-y-2">
              <Label>Reason for Return</Label>
              <Textarea
                value={returnReason}
                onChange={(e) => setReturnReason(e.target.value)}
                placeholder="Enter reason for return..."
                rows={2}
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setReturnDialogOpen(false)}>Cancel</Button>
            <Button variant="destructive" onClick={handleSubmitReturn} disabled={loading}>
              {loading ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <Undo2 className="mr-2 h-4 w-4" />}
              Process {returnType === 'full' ? 'Full' : 'Partial'} Return
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Add/Edit Payment Dialog */}
      <Dialog open={paymentDialogOpen} onOpenChange={(open) => {
        setPaymentDialogOpen(open);
        if (!open) {
          setEditingPayment(null);
          setPaymentAmount("");
          setPaymentMethod("Cash");
          setPaymentNotes("");
        }
      }}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>{editingPayment ? "Edit Payment" : "Add Payment"}</DialogTitle>
            <DialogDescription>
              {editingPayment
                ? `Editing payment of ${currency} ${parseFloat(editingPayment.amount).toFixed(2)}`
                : `Add payment for sale ${selectedSale?.order_number}. Outstanding: ${currency} ${selectedSale ? Math.max(0, parseFloat(selectedSale.total) - parseFloat(selectedSale.amount_paid || 0)).toFixed(2) : '0.00'}`}
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label>Amount ({currency})</Label>
              <Input
                type="number"
                step="0.01"
                min="0"
                value={paymentAmount}
                onChange={(e) => setPaymentAmount(e.target.value)}
                placeholder="Enter amount..."
              />
            </div>
            <div className="space-y-2">
              <Label>Payment Method</Label>
              <Select value={paymentMethod} onValueChange={setPaymentMethod}>
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="Cash">Cash</SelectItem>
                  <SelectItem value="Momo">Mobile Money</SelectItem>
                  <SelectItem value="Card">Card</SelectItem>
                  <SelectItem value="Check">Check</SelectItem>
                  <SelectItem value="Bank Transfer">Bank Transfer</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <Label>Notes (Optional)</Label>
              <Textarea
                value={paymentNotes}
                onChange={(e) => setPaymentNotes(e.target.value)}
                placeholder="Payment notes..."
                rows={2}
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setPaymentDialogOpen(false)}>Cancel</Button>
            <Button
              onClick={editingPayment ? handleUpdatePayment : handleSubmitPayment}
              disabled={loading || isPaymentSubmitting || !paymentAmount}
            >
              {(loading || isPaymentSubmitting) ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <DollarSign className="mr-2 h-4 w-4" />}
              {editingPayment ? "Update Payment" : "Add Payment"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Delete Payment Confirmation Dialog */}
      <Dialog open={!!deletePaymentId} onOpenChange={(open) => !open && setDeletePaymentId(null)}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Delete Payment</DialogTitle>
            <DialogDescription>
              Are you sure you want to delete this payment? This action cannot be undone and will restore the outstanding balance.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDeletePaymentId(null)}>Cancel</Button>
            <Button
              variant="destructive"
              onClick={handleDeletePayment}
              disabled={isPaymentSubmitting}
            >
              {isPaymentSubmitting ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <Trash2 className="mr-2 h-4 w-4" />}
              Delete Payment
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Payment History Sheet */}
      <Sheet open={paymentHistorySheetOpen} onOpenChange={setPaymentHistorySheetOpen}>
        <SheetContent className="sm:max-w-lg overflow-y-auto">
          <SheetHeader>
            <SheetTitle>Payment History</SheetTitle>
            <SheetDescription>
              {currentSale?.order_number} - Total: {currency} {currentSale ? parseFloat(currentSale.total).toFixed(2) : '0.00'}
            </SheetDescription>
          </SheetHeader>
          {currentSale && (
            <div className="space-y-6 mt-6">
              {/* Payment Summary */}
              <div className="space-y-3 p-4 bg-muted/50 rounded-lg">
                <div className="flex justify-between">
                  <span className="text-muted-foreground">Total Amount</span>
                  <span className="font-bold">{currency} {parseFloat(currentSale.total).toFixed(2)}</span>
                </div>
                <div className="flex justify-between text-green-600">
                  <span>Amount Paid</span>
                  <span className="font-bold">{currency} {parseFloat(currentSale.amount_paid || 0).toFixed(2)}</span>
                </div>
                <div className="flex justify-between text-orange-600 border-t pt-2">
                  <span>Outstanding Balance</span>
                  <span className="font-bold">{currency} {Math.max(0, parseFloat(currentSale.total) - parseFloat(currentSale.amount_paid || 0)).toFixed(2)}</span>
                </div>
                {currentSale.due_date && (
                  <div className="flex justify-between border-t pt-2">
                    <span className="text-muted-foreground">Due Date</span>
                    <span className={new Date(currentSale.due_date) < new Date() && currentSale.payment_status !== 'paid' ? 'text-red-600 font-semibold' : ''}>
                      {format(new Date(currentSale.due_date), "dd MMM yyyy")}
                      {new Date(currentSale.due_date) < new Date() && currentSale.payment_status !== 'paid' && ' (OVERDUE)'}
                    </span>
                  </div>
                )}
              </div>

              {/* Payment History Table */}
              <div className="space-y-3">
                <h4 className="font-semibold">Payment Records</h4>
                {currentSale.payments && currentSale.payments.length > 0 ? (
                  <div className="border rounded">
                    <Table>
                      <TableHeader>
                        <TableRow>
                          <TableHead>Date</TableHead>
                          <TableHead>Method</TableHead>
                          <TableHead>Received By</TableHead>
                          <TableHead className="text-right">Amount</TableHead>
                          {canManagePayments && <TableHead className="text-right">Actions</TableHead>}
                        </TableRow>
                      </TableHeader>
                      <TableBody>
                        {currentSale.payments.map((payment, idx) => (
                          <TableRow key={idx}>
                            <TableCell className="text-sm">{format(new Date(payment.payment_date), "dd MMM yyyy")}</TableCell>
                            <TableCell className="text-sm">{payment.payment_method}</TableCell>
                            <TableCell className="text-sm">
                              {payment.receivedBy?.username || payment.User?.username || 'N/A'}
                            </TableCell>
                            <TableCell className="text-right font-medium text-green-600">
                              {currency} {parseFloat(payment.amount).toFixed(2)}
                            </TableCell>
                            {canManagePayments && (
                              <TableCell className="text-right">
                                <div className="flex justify-end gap-1">
                                  <Button
                                    variant="ghost"
                                    size="sm"
                                    className="h-7 w-7 p-0"
                                    onClick={() => handleEditPayment(payment)}
                                  >
                                    <Pencil className="h-3 w-3" />
                                  </Button>
                                  <Button
                                    variant="ghost"
                                    size="sm"
                                    className="h-7 w-7 p-0 text-red-600 hover:text-red-700"
                                    onClick={() => setDeletePaymentId(payment.id)}
                                  >
                                    <Trash2 className="h-3 w-3" />
                                  </Button>
                                </div>
                              </TableCell>
                            )}
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </div>
                ) : (
                  <div className="text-center py-8 text-muted-foreground border rounded">
                    No payments recorded yet
                  </div>
                )}
              </div>

              {/* Add Payment Button */}
              {canAcceptPayments && (currentSale.payment_status === 'partial' || currentSale.payment_status === 'unpaid') && currentSale.status !== 'returned' && (canAcceptCrossShopPayments || !currentSale.shop_id || currentSale.shop_id === currentUser?.shop_id) && (
                <Button
                  className="w-full"
                  onClick={() => {
                    setPaymentHistorySheetOpen(false);
                    handleOpenPayment(currentSale);
                  }}
                >
                  <DollarSign className="mr-2 h-4 w-4" /> Record New Payment
                </Button>
              )}
            </div>
          )}
        </SheetContent>
      </Sheet>


      {/* Hidden Receipt Component for Printing */}
      <div style={{ position: 'absolute', left: '-9999px', top: '-9999px' }}>
        <Receipt
          ref={receiptRef}
          sale={currentSale}
          businessDetails={businessDetails}
          paymentStatus={currentSale?.status === 'draft' ? 'draft' : currentSale?.payment_status || 'paid'}
        />
      </div>
    </div>
  );
}
